import * as env from '@/common/environment';
import {
  buildConnectConfig,
  canConnectToAgent,
  checkAiABTestingPassed,
  checkAiGuestABTestingPassed,
  checkAiTransactionABTestingPassed,
  EventType,
  ItemType,
  mergeConnectConfig,
} from '../../lib';
import { isStartKYCFlowEvent } from '../../lib/EventType';
import { IsUserEligibleForAiFlowParams, UserId } from './AiService.types';
import { MINUTE } from '@/common/date';
import type { AiMessageForUser, BaseEvent, ConnectToAgent, InitialConnectAiEvent, MessageListItem } from '../../types';
import { AI_EVENTS, analytics } from '@/common/logs';

export const getRecentlyConnectedAiKey = (userId: UserId): string => `AI-${userId}-connect`;

export const hasRecentlyConnectedAI = (userId: UserId, conversationCooldownMins: number | null = 0): boolean => {
  if (!userId || !conversationCooldownMins) {
    return false;
  }

  const { bypassAbTestingUserIds }: { bypassAbTestingUserIds?: string[] } = env.get('AI') || {
    bypassAbTestingUserIds: [],
  };

  // Specified users should see AI without any restrictions
  if (bypassAbTestingUserIds?.includes(String(userId))) {
    return false;
  }

  // Time in milliseconds or null value
  const recentlyConnectedValue = localStorage.getItem(getRecentlyConnectedAiKey(userId));
  if (!recentlyConnectedValue) return false;

  const recentlyConnectedTimestamp = Number(recentlyConnectedValue);
  const conversationCooldownMs = conversationCooldownMins * MINUTE;

  return Date.now() - recentlyConnectedTimestamp < conversationCooldownMs;
};

export const setRecentlyConnectedAI = (userId?: UserId): void => {
  if (!userId) {
    return;
  }

  localStorage.setItem(getRecentlyConnectedAiKey(userId), String(Date.now()));
};

const BYPASS_AI_FLOW_LOYALTY_TIERS = ['diamond', 'titanium'];

const checkBypassAiFlowLoyaltyTier = (userId: UserId, userLoyaltyTier?: string): boolean => {
  if (!userId || !userLoyaltyTier) {
    return false;
  }

  const { bypassAbTestingUserIds }: { bypassAbTestingUserIds?: string[] } = env.get('AI') || {
    bypassAbTestingUserIds: [],
  };

  const bypassAbTesting = bypassAbTestingUserIds?.includes(String(userId));

  // For bypass testing users we don't need to hide AI flow
  if (bypassAbTesting) {
    return false;
  }

  const loyaltyTier = userLoyaltyTier.toLowerCase();
  return BYPASS_AI_FLOW_LOYALTY_TIERS.includes(loyaltyTier);
};

export const isUserEligibleForAiFlow = (params: IsUserEligibleForAiFlowParams): boolean => {
  const {
    userId,
    userLoyaltyTier,
    isUserLoggedIn = false,
    skipAiChat = false,
    aiEnabledPerBrand = false,
    aiEnabledPerSession = false,
    enableMetadataRules = false,
    openAiDown = false,
    abTestPercentage = 0,
    aiAbTestingEnabled = false,
    aiEndUserComeBackTime = 0,
    aiTransactionEnabled = false,
    aiTransactionAbRatio = 0,
    aiGuestUsersEnabled = false,
    aiGuestUsersAbRatio = 0,
  } = params;

  // Check if user is logged in or eligible for "Guest AI" AB Testing
  const isLoginTestPassed =
    !!isUserLoggedIn ||
    checkAiGuestABTestingPassed({
      sessionId: analytics.sessionId,
      enableAbTesting: aiGuestUsersEnabled,
      abValue: aiGuestUsersAbRatio,
    });

  const aiAbTestPassed = checkAiABTestingPassed({
    userId,
    abValue: abTestPercentage,
    enableAbTesting: aiAbTestingEnabled,
  });

  const aiTransactionAbTestPassed = checkAiTransactionABTestingPassed({
    userId,
    enableAbTesting: aiTransactionEnabled,
    abValue: aiTransactionAbRatio,
    aiAbTestPassed,
  });

  const didUserRecentlyConnectToAI = hasRecentlyConnectedAI(userId, aiEndUserComeBackTime);
  const userBypassAiFlowLoyaltyTier = checkBypassAiFlowLoyaltyTier(userId, userLoyaltyTier);
  // If AI Transaction is enabled, we won't show Metadata Automation Flow
  // TODO: include AI Bet AB Testing here to condition
  const metadataAutomationCheck = !enableMetadataRules || aiTransactionAbTestPassed || !isUserLoggedIn;

  analytics.track({
    name: AI_EVENTS.AI_CHECK_ELIGIBLE,
    metadata: {
      isLoginTestPassed,
      aiEnabledPerBrand,
      aiEnabledPerSession,
      aiAbTestPassed,
      metadataAutomationCheck,
      openAiDown,
      skipAiChat,
      didUserRecentlyConnectToAI,
      userBypassAiFlowLoyaltyTier,
    },
  });

  const isEligible = Boolean(
    isLoginTestPassed &&
    aiEnabledPerBrand &&
    aiEnabledPerSession &&
    aiAbTestPassed &&
    metadataAutomationCheck &&
    !openAiDown &&
    !skipAiChat &&
    !didUserRecentlyConnectToAI &&
    !userBypassAiFlowLoyaltyTier
  );

  return isEligible;
};

/** Internal events: use `payload.selected` when it is a connect-shaped object; otherwise `message.config.connect` (e.g. preSupport). */
export const getInternalEventConnectConfig = (event: BaseEvent): ConnectToAgent | undefined => {
  const selected = event.payload?.selected;
  const aiConversation = event.message?.config?.connect?.aiConversation;
  if (selected !== null && typeof selected === 'object') {
    return { ...selected, ...(aiConversation ? { aiConversation } : {}) } as ConnectToAgent;
  }
  return event.message?.config?.connect as ConnectToAgent | undefined;
};

export const getAiEventConfig = (event: InitialConnectAiEvent) => {
  const isExternalEvent = EventType.isExternalEvent(event);
  const connectConfig = (
    isExternalEvent ? event.payload.supportOptions : getInternalEventConnectConfig(event as BaseEvent)
  ) as ConnectToAgent;

  return connectConfig;
};

export const getAiHardcodedConnectConfig = () => {
  return buildConnectConfig({
    connectToAgent: true,
    showDescriptionBox: true,
    loginRequired: true,
    showLiveChat: true,
    showVoiceChat: true,
    showOpenRequest: true,
  });
};

// Here we create connect config for finish flow based on the ability to connect to agent.
// If we can connect to agent, we'll show existing options, otherwise we'll all hardcoded options.
export const getAiFinishFlowConfig = (connectConfig: ConnectToAgent) => {
  const ableToConnectToAgent = canConnectToAgent(connectConfig);
  const hardcodedAllowConnectConfig = getAiHardcodedConnectConfig();

  return ableToConnectToAgent ? connectConfig : mergeConnectConfig(connectConfig, hardcodedAllowConnectConfig);
};

export const getAiEventFinishFlowConfig = (event: InitialConnectAiEvent) => {
  const connectConfig = getAiEventConfig(event);
  return getAiFinishFlowConfig(connectConfig);
};

export const getAiGuestStartFlowConfig = (connectConfig: ConnectToAgent = buildConnectConfig({})) => {
  const ableToConnectToAgent = canConnectToAgent(buildConnectConfig({ ...connectConfig }));
  return ableToConnectToAgent
    ? mergeConnectConfig(connectConfig, {
        ...buildConnectConfig(connectConfig),
        loginMessageHandled: true,
        aiConversation: true,
      })
    : mergeConnectConfig(connectConfig, {
        ...buildConnectConfig(connectConfig),
        connect: true,
        liveChat: true,
        loginRequired: false,
        loginMessageHandled: true,
        aiConversation: true,
      });
};

export const getAiGuestFallbackConfig = () => {
  return buildConnectConfig({
    connectToAgent: true,
    showDescriptionBox: true,
    loginRequired: false,
    // We'll allow only live chat for now
    showLiveChat: true,
    showVoiceChat: false,
    showOpenRequest: false,
  });
};

export const isInKycAiFlowFromMessages = (messages: MessageListItem[]): boolean => {
  const initialMessage = messages.find(
    (m): m is AiMessageForUser => ItemType.isAiMessageForUser(m) && Boolean(m.initialEvent)
  );
  const initialEvent = initialMessage?.initialEvent;
  if (!initialEvent) {
    return false;
  }
  return isStartKYCFlowEvent(initialEvent);
};
